import { getTokenFromLS } from "../account/tokenUtils";

const CLIENT_ID_KEY = "mesfClientTelemetryClientId";
const SESSION_ID_KEY = "mesfClientTelemetrySessionId";

export type ClientTelemetryEventType =
	| "session_start"
	| "heartbeat"
	| "environment_change"
	| "route_view"
	| "visibility_visible"
	| "visibility_hidden"
	| "logout";

export type ClientTelemetryPayload = {
	eventType: ClientTelemetryEventType;
	path?: string;
	previousPath?: string;
	payload?: Record<string, unknown>;
	keepalive?: boolean;
};

const base =
	document.getElementsByTagName("base")[0]?.getAttribute("href") || "/";

const createId = () => {
	if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
		return crypto.randomUUID();
	}

	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (value) => {
		const random = Math.floor(Math.random() * 16);
		const output = value === "x" ? random : (random & 0x3) | 0x8;
		return output.toString(16);
	});
};

export const getClientTelemetryClientId = () => {
	let clientId = localStorage.getItem(CLIENT_ID_KEY);
	if (!clientId) {
		clientId = createId();
		localStorage.setItem(CLIENT_ID_KEY, clientId);
	}

	return clientId;
};

export const getClientTelemetrySessionId = () => {
	let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
	if (!sessionId) {
		sessionId = createId();
		sessionStorage.setItem(SESSION_ID_KEY, sessionId);
	}

	return sessionId;
};

const joinUrl = (root: string, path: string) => {
	const normalizedRoot = root.endsWith("/") ? root.slice(0, -1) : root;
	const normalizedPath = path.startsWith("/") ? path : `/${path}`;
	return `${normalizedRoot}${normalizedPath}`;
};

export const sendClientTelemetry = async ({
	eventType,
	path,
	previousPath,
	payload,
	keepalive = false,
}: ClientTelemetryPayload) => {
	const token = getTokenFromLS();
	if (!token) {
		return;
	}

	try {
		const { collectBrowserTelemetry } = await import(
			"./collectBrowserTelemetry"
		);
		const browserTelemetry = collectBrowserTelemetry();

		await fetch(joinUrl(base, "/Telemetry/client"), {
			method: "POST",
			mode: "cors",
			keepalive,
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({
				clientId: getClientTelemetryClientId(),
				sessionId: getClientTelemetrySessionId(),
				eventType,
				...browserTelemetry,
				path: path ?? window.location.pathname + window.location.search,
				previousPath,
				payloadJson: payload ? JSON.stringify(payload) : undefined,
			}),
		});
	} catch {
		// Telemetry must never affect application behavior.
	}
};
